import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { fetchGoogleDriveStatus } from '../services/googleDriveIntegrationAPI';
import { getCurrentEntityId } from '../services/accountSwitcher';
import { isAdminRole } from '../utils/permissionUtils';
import { isImpersonationSession } from '../utils/impersonation';

const googleDriveStatusQueryKey = ['google-drive-status'] as const;

function readStoredUser(): any {
    try {
        return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
        return {};
    }
}

/**
 * Admin roles and god mode, portal web only (hidden on native and Aria standalone, and while impersonating).
 * The server enforces the same rule on every Drive route.
 */
export function canUseGoogleDriveFromStorage(): boolean {
    if (Capacitor.isNativePlatform() || isImpersonationSession()) return false;
    const user = readStoredUser()?.user;
    if (!user || user.platformContext === 'aria_chat' || user.isPrivilegedImpersonation === true) return false;
    return user.isGodModeUser === true || isAdminRole(user.role);
}

export function useGoogleDriveStatus(enabled = true) {
    // Read once per mount; callers re-render often (e.g. while an artifact streams)
    const { accountKey, allowed } = useMemo(() => {
        const stored = readStoredUser();
        return {
            accountKey: [String(stored?.user?.userId || ''), getCurrentEntityId(stored)],
            allowed: canUseGoogleDriveFromStorage(),
        };
    }, []);
    return useQuery({
        // Per account + entity so an account switch never reuses another role's status
        queryKey: [...googleDriveStatusQueryKey, ...accountKey],
        queryFn: fetchGoogleDriveStatus,
        enabled: enabled && allowed,
        staleTime: 5 * 60 * 1000,
    });
}

export function useInvalidateGoogleDriveStatus() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: googleDriveStatusQueryKey });
}
